import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditBlogSlideModal from 'components/admin/common/modal/Blog/EditBlogSlideModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';
import usePost from 'core/hooks/api/usePost';
import useDelete from 'core/hooks/api/useDelete';

const BlogSlideSection = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createSlideSectionData,
        postError: creatingError,
        postLoading: creating,
        putData: updateSlideSectionData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteSlideSectionData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.BLOG_SLIDE_SECTIONS);

    const { fetchData: blogData } = useCrud(API_ENDPOINTS.BLOGS);
    const { postData: createSliderImages, loading: uploadingImages } = usePost(API_ENDPOINTS.BLOG_SLIDER_IMAGES);
    const { deleteData: deleteSliderImage } = useDelete(API_ENDPOINTS.BLOG_SLIDER_IMAGES);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteSlideSection = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditSlideSection = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteSlideSection, modalData: slideSectionToDelete, openModal: openDeleteSlideSection, closeModal: closedDeleteSlideSection } = useModalState({
        onClose: handleCloseDeleteSlideSection
    });

    const { isOpen: isOpenEditSlideSection, modalData: selectedSlideSection, openModal: openEditSlideSection, closeModal: closedEditSlideSection } = useModalState({
        onClose: handleCloseEditSlideSection
    });

    const validationRules = [
        { field: 'blog_id', method: 'required' },
        { field: 'section_subtitle', method: 'required' },
        { field: 'section_title', method: 'required' },
        { field: 'background_image', method: ['required', 'imageFormat'] }
    ];

    const blogOptions = useMemo(() => {
        if (blogData?.length > 0) {
            return blogData.map(blog => ({
                label: blog.section_title,
                value: blog.id
            }));
        } else {
            return [{ label: 'Default', value: 1 }, { label: 'Default', value: 2 }];
        }
    }, [blogData]);

    const handledeleteSliderImage = async (id) => {
        try {
            await deleteSliderImage(id);
            refetch();
        } catch (error) {
            console.error(error);
        }
    };

    const handleModalSave = useCallback(async (updatedSlideSection, galleryImages) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedSlideSection, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        setErrors({});
        const forms = galleryImages
        ?.filter((image) => {
            const fileType = image.file.type || image.file.path.split('.').pop();
            return image.file && image.file.path && !image.file.path.startsWith('storage/') && (fileType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif'].includes(fileType));
        })
        ?.map((image) => ({
                slide_section_id: updatedSlideSection.id || null,
                src: image.file,
                position: 1,
            }));

        try {
            if (updatedSlideSection.id) {
                const { media_item, slide_sections, ...slideSectionData } = updatedSlideSection;
                await updateSlideSectionData(updatedSlideSection.id, slideSectionData, true);
                if (forms?.length > 0) {
                    for (const form of forms) {
                        form.slide_section_id = updatedSlideSection.id;
                        await createSliderImages(form, true);
                    }
                }
                setSuccessMessage("Sección de diapositivas actualizada con éxito.");
            } else {
                const createdSlideSection = await createSlideSectionData(updatedSlideSection, true);
                if (forms?.length > 0) {
                    for (const form of forms) {
                        form.slide_section_id = createdSlideSection.id;
                        await createSliderImages(form, true);
                    }
                }
                setSuccessMessage("Sección de diapositivas creada con éxito.");
            }
            closedEditSlideSection();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateSlideSectionData, createSlideSectionData, createSliderImages, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newSlideSection = {
            section_subtitle: '',
            section_title: '',
            background_image: '',
            blog_id: ''
        };
        openEditSlideSection(newSlideSection);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (slideSectionToDelete) {
            try {
                await deleteSlideSectionData(slideSectionToDelete.id);
                setSuccessMessage("Sección de diapositivas eliminada con éxito.");
                refetch();
                closedDeleteSlideSection();
            } catch (error) {
                console.error(error);
            }
        }
    }, [slideSectionToDelete, deleteSlideSectionData, refetch]);

    const columns = useMemo(() => [
        { field: 'section_title', header: 'Título de la Sección', sortable: true },
        { field: 'section_subtitle', header: 'Subtítulo de la Sección', sortable: true },
        { field: 'background_image', header: 'Imagen de Fondo', sortable: true }
    ], []);

    const memoizedGalleryImages = useMemo(() => selectedSlideSection?.slider_images || [], [selectedSlideSection]);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Sección de Diapositivas del Blog</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Sección de Diapositivas
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditSlideSection}
                onDelete={openDeleteSlideSection}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="section_title"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditBlogSlideModal
                show={isOpenEditSlideSection}
                slideSection={selectedSlideSection}
                handleClose={closedEditSlideSection}
                handleSave={handleModalSave}
                updating={updating || creating || uploadingImages}
                errors={errors}
                requestError={creatingError || updatingError}
                deleteSliderImage={handledeleteSliderImage}
                initialImages={memoizedGalleryImages}
                blogOptions={blogOptions}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteSlideSection}
                onHide={closedDeleteSlideSection}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(BlogSlideSection);